import { use, useEffect, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import hapticsOnPress from '@/utils/hapticsOnPress';
import Pressable from '../Unistyles/Pressable';
import { NavScrollContext } from './Nav';

export interface NavItemArgs {
  children: React.ReactNode;
  index: number;
  isFocused?: boolean;
  onPress?: () => void;
}

export function Label({ children }: { children: React.ReactNode }) {
  return <Text style={style.text}>{children}</Text>;
}

export function Container({
  children,
  index: _index,
  isFocused,
  onPress,
}: NavItemArgs) {
  const nav = use(NavScrollContext);
  const [layout, setLayout] = useState({ x: 0, width: 0 });

  const onLayout = (event: LayoutChangeEvent) => {
    const { x, width } = event.nativeEvent.layout;
    const measured = { x, width };
    setLayout((previous) =>
      previous.x === measured.x && previous.width === measured.width
        ? previous
        : measured,
    );
  };

  useEffect(() => {
    if (!isFocused || !nav) {
      return;
    }

    const scrollView = nav.scrollRef.current;
    if (!scrollView || nav.viewportWidth <= 0 || layout.width <= 0) {
      return;
    }

    const center = layout.x + layout.width / 2 - nav.viewportWidth / 2;
    const maxScroll = Math.max(0, nav.contentWidth - nav.viewportWidth);
    scrollView.scrollTo({
      x: Math.max(0, Math.min(center, maxScroll)),
      animated: true,
    });
  }, [
    isFocused,
    layout.x,
    layout.width,
    nav,
    nav?.contentWidth,
    nav?.viewportWidth,
  ]);

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{ selected: Boolean(isFocused) }}
      style={style.container}
      onLayout={onLayout}
      onPress={hapticsOnPress(onPress)}
    >
      {children}
      {isFocused ? (
        <Animated.View
          entering={FadeInDown.damping(10).duration(200)}
          style={style.indicator}
        />
      ) : null}
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.size(3),
    borderWidth: 0,
    borderRadius: theme.borderRadius.sm,
    gap: theme.space('md'),
  },
  text: {
    fontSize: theme.fontSize.xl,
  },
  indicator: {
    backgroundColor: theme.colors.surfaceInverted,
    height: theme.size(1),
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
}));
